import {useEffect, useRef, useState} from "react";
import Modal from "react-bootstrap/Modal";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import * as echarts from "echarts";
import moment from "moment";
import axiApi from "ApiCaller";
import DateUtils from "core/utils/DateUtils";
import DropdownDateRange from "components/datetime/DropdownDateRange";
import {AlertsChartsParams, AlertsTypes} from "site-apps/alerts/AlertsTypesCharts";

const RANGE_DAYS = 7;
const MAX_DAYS = 30;

// Curvas de la gráfica: nombre, campo del punto y estilo de la línea; `area`: relleno degradado bajo la curva
const CURVES = [
    {name: "Real", field: "real", color: "rgb(0,151,255)", type: "solid", width: 2, symbol: true, area: true},
    {name: "Esperado", field: "expected", color: "#6c757d", type: "dashed", width: 2, symbol: false, area: false},
    {name: "P90 C3", field: "p90", color: "#dc3545", type: "dashed", width: 2, symbol: false, area: false},
];
const UNLOAD_NAME = "Vaciado";
const UNLOAD_COLOR = AlertsChartsParams.getAlertColor(AlertsTypes.UNLOAD);

const legendColor = (name) => CURVES.find((curve) => curve.name === name)?.color || UNLOAD_COLOR;

// Degradado vertical del color de la curva a transparente
const areaGradient = (color) => ({
    color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
        {offset: 0, color: color.replace("rgb(", "rgba(").replace(")", ",0.35)")},
        {offset: 1, color: color.replace("rgb(", "rgba(").replace(")", ",0.02)")},
    ]),
});

// Pie de la gráfica: de dónde sale cada curva, sin fórmulas
const LEGEND_TEXTS = [
    {
        name: "Real",
        text:
            "Lo que ha subido el contenedor desde el inicio de cada día, sumando lo que sube o baja en cada lectura. " +
            "Las bajadas grandes, como un vaciado, no restan. A medianoche vuelve a cero.",
    },
    {
        name: "Esperado",
        text:
            "Lo que se esperaba que subiera ese día a cada hora: lo que suele subir un día como ese de la semana " +
            "(mediana de las últimas semanas), repartido por horas según cómo se ha llenado normalmente este contenedor.",
    },
    {
        name: "P90 C3",
        text:
            "El peor caso razonable: lo esperado multiplicado por lo que se desvían los días más altos de este " +
            "contenedor (solo 1 de cada 10 días ha subido más que esto). Con pocos días de datos se usa el de su " +
            "fracción. Si a mañana se llega por encima del umbral con esta curva, C3 manda recogerlo hoy.",
    },
    {name: UNLOAD_NAME, text: "Momento en que se detectó que el contenedor se vació."},
];
const LEGEND_NOTE = "Cada día se compara con el modelo que se calculó ese mismo día, el que se usó para decidir.";

const SOURCES = {own: "propio", fallback: "de la fracción"};

const defaultRange = () => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(endDate.getDate() - RANGE_DAYS);
    return {startDate, endDate};
};

const num = (value, suffix = "%") => (value === null || value === undefined ? "-" : `${value}${suffix}`);

// Datos del modelo de un día para el tooltip
const modelText = (model) => {
    if (!model) return "Sin modelo ese día";
    const p90 = model.residual_p90 === null ? "-" : `×${model.residual_p90} (${SOURCES[model.residual_p90_source] || "-"})`;
    return (
        `Días válidos: ${model.valid_days}<br/>` +
        `VDM 30: ${num(model.baseline_rate)} · Ritmo del día: ${num(model.rate)}<br/>` +
        `P90: ${p90}`
    );
};

/*
 * Delta horario acumulado de un dispositivo por día frente al modelo guardado de cada día (esperado y P90 de C3).
 * Autónomo: solo necesita el uuid. Datos de /api/distribution/dynamic/overflow/delta/<uuid>/.
 */
const ModalOverflowDelta = ({show, setShow, uuid, title}) => {
    const chartContainer = useRef(null);
    const chartInstance = useRef(null);
    const [dateRange, setDateRange] = useState(defaultRange);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const handleClose = () => {
        setShow(false);
    };

    // Al cerrar: se libera la gráfica y se vuelve al rango por defecto
    useEffect(() => {
        if (!show) {
            chartInstance.current?.dispose();
            chartInstance.current = null;
            setData(null);
            setDateRange(defaultRange());
        }
    }, [show]);

    useEffect(() => {
        if (!show || !uuid || !dateRange) return;
        setLoading(true);
        const params = {
            start: DateUtils.dateToStringInput(dateRange.startDate),
            end: DateUtils.dateToStringInput(dateRange.endDate),
        };
        axiApi
            .get(`/api/distribution/dynamic/overflow/delta/${uuid}/`, {params: params})
            .then((resp) => setData(resp.data))
            .catch(() => setData({points: [], models: {}}))
            .finally(() => setLoading(false));
    }, [show, uuid, dateRange]);

    useEffect(() => {
        if (!data || !data.points?.length || !chartContainer.current) return;
        // Tras un rango sin datos el contenedor es otro: se vuelve a crear la gráfica
        if (chartInstance.current && chartInstance.current.getDom() !== chartContainer.current) {
            chartInstance.current.dispose();
            chartInstance.current = null;
        }
        if (!chartInstance.current) {
            chartInstance.current = echarts.init(chartContainer.current, null, {renderer: "canvas"});
        }
        const points = data.points;
        const models = data.models || {};

        const series = CURVES.map((curve) => ({
            name: curve.name,
            type: "line",
            showSymbol: curve.symbol,
            symbol: "circle",
            symbolSize: 4,
            connectNulls: false,
            itemStyle: {color: curve.color},
            lineStyle: {color: curve.color, type: curve.type, width: curve.width},
            areaStyle: curve.area ? areaGradient(curve.color) : undefined,
            emphasis: {focus: "series"},
            data: points.map((point) => [new Date(point.time * 1000), point[curve.field]]),
        }));
        series.push({
            name: UNLOAD_NAME,
            type: "scatter",
            symbol: "circle",
            symbolSize: 12,
            itemStyle: {color: UNLOAD_COLOR, borderColor: "#fff", borderWidth: 2},
            data: points.filter((point) => point.unload).map((point) => [new Date(point.time * 1000), point.real]),
            z: 10,
        });

        chartInstance.current.setOption(
            {
                tooltip: {
                    trigger: "axis",
                    formatter: (params) => {
                        const line = params.find((param) => param.seriesType === "line");
                        if (!line) return "";
                        const point = points[line.dataIndex];
                        let result = moment(point.time * 1000).format("DD-MM-YYYY HH:mm") + "<br/>";
                        params
                            .filter((param) => param.seriesType === "line")
                            .forEach((param) => {
                                result += `${param.marker} ${param.seriesName}: ${num(param.value[1])}<br/>`;
                            });
                        if (point.unload) result += `${UNLOAD_NAME}<br/>`;
                        return result + "<hr class='my-1'/>" + modelText(models[point.day]);
                    },
                },
                legend: {data: [...CURVES.map((curve) => curve.name), UNLOAD_NAME], top: "top", icon: "roundRect"},
                toolbox: {feature: {restore: {}, saveAsImage: {name: `delta_${uuid}`}}},
                grid: {left: "17px", right: "40px", bottom: "40px", containLabel: true},
                xAxis: {
                    type: "time",
                    splitLine: {show: true, lineStyle: {color: "#d8d8d8", width: 1, type: "dashed"}},
                    axisLabel: {formatter: "{dd}-{MM} {HH}:{mm}", hideOverlap: true},
                },
                yAxis: {type: "value", min: 0, axisLabel: {formatter: "{value}%"}},
                dataZoom: [
                    {type: "inside", start: 0, end: 100},
                    {type: "slider", start: 0, end: 100, bottom: 10, height: 20, showDetail: false},
                ],
                series: series,
            },
            true,
        );
        setTimeout(() => chartInstance.current?.resize(), 100);
    }, [data, uuid]);

    const empty = data && !data.points?.length;

    return (
        <Modal show={show} onHide={handleClose} size="xl">
            <Modal.Header closeButton className="preview-canvas mb-0">
                <div className="preview-icon">
                    <FontAwesomeIcon icon="chart-line" />
                </div>
                <div className="preview-title d-inline-flex align-items-center">
                    <Modal.Title className="d-inline-flex ms-1">
                        <div>
                            <div className="h4 ms-0">Delta diario</div>
                            <div className="h5 ms-0">
                                <small>{title}</small>
                            </div>
                        </div>
                    </Modal.Title>
                    <div className="ms-5" style={{minWidth: "260px"}}>
                        <DropdownDateRange
                            size="sm"
                            dateRange={dateRange}
                            setDateRange={setDateRange}
                            maxDays={MAX_DAYS}
                            range={RANGE_DAYS}
                        />
                    </div>
                    {loading && <FontAwesomeIcon icon="circle-notch" spin className="ms-3 text-primary" />}
                </div>
            </Modal.Header>
            <Modal.Body>
                {empty ? (
                    <div className="alert alert-secondary">Sin datos en el rango</div>
                ) : (
                    <div ref={chartContainer} style={{width: "100%", height: "420px"}} />
                )}
                <div className="card bg-form border-0 mt-3">
                    <div className="card-body py-2 small">
                        {LEGEND_TEXTS.map((legend) => (
                            <div key={legend.name} className="d-flex mb-1">
                                <span
                                    className="flex-shrink-0 me-2 mt-1 rounded-circle"
                                    style={{width: "10px", height: "10px", backgroundColor: legendColor(legend.name)}}
                                />
                                <div>
                                    <strong>{legend.name}:</strong> {legend.text}
                                </div>
                            </div>
                        ))}
                        <div className="mt-2 text-muted fst-italic">{LEGEND_NOTE}</div>
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default ModalOverflowDelta;
